// Alpha-mask render helper extracted 1:1 from RoomCanvas.tsx.
// Operates directly on a PIXI.Sprite (mutates it) — no React state.
// Pure AlphaMask type lives in domain; this module applies it to PIXI sprites.
import type * as PIXI from 'pixi.js';
import {
  OPAQUE_PIXEL_ALPHA_THRESHOLD,
  type AlphaMask,
} from '../../../domain/alphaMask';
import { asFurnitureSprite } from './pixiExtensions';
export { OPAQUE_PIXEL_ALPHA_THRESHOLD, type AlphaMask };

// ── Alpha-mask offset computation (extracted so it can run lazily/deferred) ──
// Scans the opaque pixels of a furniture image to:
//  • align the last opaque row to the marker anchor (floor-contact point),
//  • horizontally center on the opaque support region,
//  • (windows only) measure the widest opaque row for footprint estimation.
// Sets the __visual* offsets and stores the mask on the sprite as __alphaMask.
// The sprite's hitArea reads __alphaMask dynamically, so once this runs the hit
// test becomes pixel-perfect WITHOUT recreating the closure. Until it runs the
// hitArea falls back to the sprite bounding-box (interaction still works).
//
// CRITICAL: this only changes WHEN the offsets are applied, never the final values.
// For rugs/windows the depth/zIndex depends on these offsets, so callers must
// run this synchronously (before depth) for those; regular furniture defers it.
export const applyAlphaMaskToSprite = (
  sprite: PIXI.Sprite,
  alphaMask: AlphaMask | null,
  isWindowItem: boolean,
  isMirrored: boolean,
): void => {
  const fs = asFurnitureSprite(sprite)!;
  fs.__alphaMask = alphaMask ?? null;

  if (alphaMask && alphaMask.width > 0 && alphaMask.height > 0) {
    let bottomOpaqueY = -1;
    let bottomMinX = alphaMask.width;
    let bottomMaxX = -1;
    let maxOpaqueRowWidth = 0;
    for (let py = alphaMask.height - 1; py >= 0; py--) {
      let rowMinX = alphaMask.width;
      let rowMaxX = -1;
      for (let px = 0; px < alphaMask.width; px++) {
        const a = alphaMask.data[py * alphaMask.width + px];
        if (a > OPAQUE_PIXEL_ALPHA_THRESHOLD) {
          if (px < rowMinX) rowMinX = px;
          if (px > rowMaxX) rowMaxX = px;
        }
      }
      if (rowMaxX >= rowMinX) {
        if (bottomOpaqueY < 0) {
          bottomOpaqueY = py;
          bottomMinX = rowMinX;
          bottomMaxX = rowMaxX;
          if (!isWindowItem) break; // windows need full scan for max width
        }
        const rowWidth = rowMaxX - rowMinX + 1;
        if (rowWidth > maxOpaqueRowWidth) maxOpaqueRowWidth = rowWidth;
      }
    }

    if (bottomOpaqueY >= 0) {
      const transparentBottomPx = Math.max(0, (alphaMask.height - 1) - bottomOpaqueY);
      const visualBottomOffset = (transparentBottomPx / alphaMask.height) * Math.abs(sprite.height);
      const supportCenterX = (bottomMinX + bottomMaxX) / 2;
      const textureCenterX = (alphaMask.width - 1) / 2;
      const supportDeltaXTexture = supportCenterX - textureCenterX;
      const visualOffsetX = -(supportDeltaXTexture / alphaMask.width) * Math.abs(sprite.width);
      // When mirrored (scale.x < 0), the support center is on the opposite side of the
      // texture center, so we must invert the X offset to keep the support aligned with
      // the marker (grid anchor) position.
      const effectiveOffsetX = isMirrored ? -visualOffsetX : visualOffsetX;
      sprite.x = effectiveOffsetX;
      sprite.y = visualBottomOffset;
      fs.__visualBaseOffsetX = visualOffsetX;
      fs.__visualOffsetX = effectiveOffsetX;
      fs.__visualBottomOffset = visualBottomOffset;
      fs.__maxOpaqueWidthPx = isWindowItem
        ? (maxOpaqueRowWidth / alphaMask.width) * Math.abs(sprite.width)
        : 0;
    } else {
      fs.__visualBaseOffsetX = 0;
      fs.__visualOffsetX = 0;
      fs.__visualBottomOffset = 0;
      fs.__maxOpaqueWidthPx = 0;
    }
  } else {
    fs.__visualBaseOffsetX = 0;
    fs.__visualOffsetX = 0;
    fs.__visualBottomOffset = 0;
    fs.__maxOpaqueWidthPx = 0;
  }
};
